import React, { useMemo, useState } from 'react'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    CircularProgress,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import AddIcon from '@material-ui/icons/Add'

import { BucketFieldList } from './BucketFieldList'
import { countBuilderBucketFields } from './workbookFieldUtils'
import type { WorkbookBucketConfig } from './workbook.types'
import { useApplicationWorkbookBuilderState } from './useApplicationWorkbookBuilderState'
import { WORKBOOK_BUCKET_LABELS } from './workbookCatalog'

const MAX_CUSTOM_SECTIONS = 5

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    bucketPanel: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        '&:before': {
            display: 'none',
        },
        boxShadow: 'none',
        '&.Mui-expanded': {
            margin: 0,
        },
    },
    bucketSummary: {
        padding: theme.spacing(1.5, 2),
        minHeight: 56,
        '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            justifyContent: 'space-between',
            margin: 0,
        },
    },
    bucketSummaryMain: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.25),
        flex: 1,
        minWidth: 0,
    },
    bucketTitle: {
        fontWeight: 600,
    },
    bucketSubtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
    },
    bucketSubtitleExcluded: {
        color: theme.palette.text.disabled,
        fontStyle: 'italic',
        fontSize: '0.875rem',
    },
    bucketDetails: {
        padding: theme.spacing(0, 2, 2),
        display: 'block',
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    includeButton: {
        flexShrink: 0,
        marginLeft: theme.spacing(2),
        marginRight: theme.spacing(1),
    },
    toolbar: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    lockedNote: {
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
    },
    addCustomRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    addHint: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
}))

interface ApplicationWorkbookBuilderTabProps {
    scope: 'property' | 'org'
    apartmentId?: number | null
    organizationId?: number | null
    workbookId: number | null | undefined
}

function bucketSummarySubtitle(bucket: WorkbookBucketConfig): string {
    if (!bucket.enabled) {
        return 'Not included in the application'
    }
    const { enabledCount, requiredCount } = countBuilderBucketFields(bucket)
    return `${enabledCount} field${
        enabledCount === 1 ? '' : 's'
    }, ${requiredCount} required`
}

function sectionTitle(bucket: WorkbookBucketConfig): string {
    if (bucket.label) return bucket.label
    if (bucket.kind === 'platform') {
        return (
            WORKBOOK_BUCKET_LABELS[
                bucket.bucket_id as keyof typeof WORKBOOK_BUCKET_LABELS
            ] ?? bucket.bucket_id
        )
    }
    return bucket.bucket_id
}

function newCustomId(prefix: string): string {
    const rand =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
            : `${Date.now().toString(36)}${Math.random()
                  .toString(36)
                  .slice(2, 8)}`
    return `${prefix}${rand}`
}

export const ApplicationWorkbookBuilderTab: React.FC<ApplicationWorkbookBuilderTabProps> = ({
    scope,
    apartmentId,
    organizationId,
    workbookId,
}) => {
    const classes = useStyles()
    const {
        definition,
        isLoading,
        isEditable,
        dirty,
        updateBucket,
        setDefinition,
        save,
        isSaving,
    } = useApplicationWorkbookBuilderState({
        scope,
        apartmentId,
        organizationId,
        workbookId,
    })

    const [customLabel, setCustomLabel] = useState('')

    const buckets = definition?.buckets ?? []
    const customCount = buckets.filter((b) => b.kind === 'custom').length

    const bucketById = useMemo(() => {
        const map = new Map<string, WorkbookBucketConfig>()
        buckets.forEach((bucket) => map.set(bucket.bucket_id, bucket))
        return map
    }, [buckets])

    if (workbookId == null) {
        return (
            <Typography color="textSecondary">
                Select or create an application workbook to edit.
            </Typography>
        )
    }

    if (isLoading || !definition) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress size={28} />
            </Box>
        )
    }

    const handleBucketIncludeChange = (
        bucketId: string,
        enabled: boolean,
        event: React.MouseEvent,
    ) => {
        event.stopPropagation()
        const bucket = bucketById.get(bucketId)
        if (!bucket) return
        updateBucket(bucketId, { enabled })
    }

    const handleAddCustomSection = () => {
        if (customCount >= MAX_CUSTOM_SECTIONS) return
        const label = customLabel.trim() || `Custom section ${customCount + 1}`
        const bucket_id = newCustomId('custom_')
        setDefinition({
            ...definition,
            buckets: [
                ...definition.buckets,
                {
                    bucket_id,
                    label,
                    kind: 'custom',
                    enabled: true,
                    fields: [],
                },
            ],
        })
        setCustomLabel('')
    }

    return (
        <Box className={classes.root}>
            {!isEditable && (
                <Typography className={classes.lockedNote}>
                    Read-only — linked to an active leasing season. Copy the
                    workbook to make changes.
                </Typography>
            )}

            {buckets.map((bucket) => {
                const bucketId = bucket.bucket_id
                return (
                    <Accordion key={bucketId} className={classes.bucketPanel}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            className={classes.bucketSummary}
                        >
                            <Box className={classes.bucketSummaryMain}>
                                <Typography
                                    variant="subtitle1"
                                    className={classes.bucketTitle}
                                >
                                    {sectionTitle(bucket)}
                                    {bucket.kind === 'custom'
                                        ? ' (custom)'
                                        : ''}
                                </Typography>
                                <Typography
                                    className={
                                        bucket.enabled
                                            ? classes.bucketSubtitle
                                            : classes.bucketSubtitleExcluded
                                    }
                                >
                                    {bucketSummarySubtitle(bucket)}
                                </Typography>
                            </Box>
                            <Button
                                className={classes.includeButton}
                                variant={
                                    bucket.enabled ? 'outlined' : 'contained'
                                }
                                color={bucket.enabled ? 'default' : 'primary'}
                                size="small"
                                disabled={!isEditable}
                                onClick={(e) =>
                                    handleBucketIncludeChange(
                                        bucketId,
                                        !bucket.enabled,
                                        e,
                                    )
                                }
                            >
                                {bucket.enabled
                                    ? 'Exclude section'
                                    : 'Include section'}
                            </Button>
                        </AccordionSummary>
                        <AccordionDetails className={classes.bucketDetails}>
                            {bucket.enabled ? (
                                <BucketFieldList
                                    bucket={bucket}
                                    readOnly={!isEditable}
                                    onChange={(updated) =>
                                        updateBucket(bucketId, updated)
                                    }
                                />
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    This section is not shown to applicants.
                                    Include it to choose which fields to
                                    collect.
                                </Typography>
                            )}
                        </AccordionDetails>
                    </Accordion>
                )
            })}

            {isEditable ? (
                <Box className={classes.addCustomRow}>
                    <TextField
                        size="small"
                        variant="outlined"
                        label="New custom section"
                        value={customLabel}
                        onChange={(e) => setCustomLabel(e.target.value)}
                        disabled={customCount >= MAX_CUSTOM_SECTIONS}
                    />
                    <Button
                        color="primary"
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        disabled={customCount >= MAX_CUSTOM_SECTIONS}
                        onClick={handleAddCustomSection}
                    >
                        Add section
                    </Button>
                    <Typography className={classes.addHint}>
                        {customCount}/{MAX_CUSTOM_SECTIONS} custom sections
                    </Typography>
                </Box>
            ) : null}

            <Box className={classes.toolbar}>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={!dirty || !isEditable || isSaving}
                    onClick={() => save()}
                >
                    {isSaving ? 'Saving…' : 'Save workbook'}
                </Button>
            </Box>
        </Box>
    )
}
